import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Flame, Send, Loader2, Mail, Sparkles } from "lucide-react";
import { api } from "../lib/api";

/**
 * Admin panel for Rising-based notifications:
 *   • Creator rising alerts (per-film, per iso-week) — fires daily via cron;
 *     admin can force-run out of cycle.
 *   • Weekly viewer digest — auto-sends every Sunday to newsletter subscribers;
 *     admin can force-send early or resend.
 * Shows the last N sent notifications for audit / reassurance.
 */
export default function NotificationsPanel() {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningDigest, setRunningDigest] = useState(false);
  const [runningCreator, setRunningCreator] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/notifications/log?limit=25");
      setLog(r.data || []);
    } catch {
      setLog([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runDigest = async () => {
    if (!window.confirm("Force-send the weekly digest to ALL active newsletter subscribers now? Ignores the 'already sent this week' guard.")) return;
    setRunningDigest(true);
    try {
      const { data } = await api.post("/admin/notifications/weekly-digest/send");
      if (data.recipients === 0) toast.info("Nothing sent — no active subscribers or no rising films to feature.");
      else toast.success(`Weekly digest sent to ${data.recipients} viewer${data.recipients === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send.");
    } finally { setRunningDigest(false); }
  };

  const runCreator = async () => {
    setRunningCreator(true);
    try {
      const { data } = await api.post("/admin/notifications/creator-rising/send");
      if (data.sent === 0) toast.info("No new creator alerts to send this week (already notified or no qualifying rising films).");
      else toast.success(`Emailed ${data.sent} creator${data.sent === 1 ? "" : "s"} about their rising film.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send.");
    } finally { setRunningCreator(false); }
  };

  return (
    <div className="card-surface p-5 sm:p-6 mb-6" data-testid="notifications-panel">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
          <Bell className="h-5 w-5 text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-eyebrow text-red-300/80">Rising notifications</p>
          <h3 className="font-display text-xl font-bold">Auto-alerts & weekly digest</h3>
          <p className="text-xs text-white/40 mt-1 max-w-2xl">
            Creators are emailed automatically when their film enters the Rising rail (idempotent per film+week).
            The viewer digest goes out every Sunday. Force-send below for testing or off-cycle campaigns.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <button
          onClick={runCreator}
          disabled={runningCreator}
          className="ghost-btn hover:border-red-500/50 hover:text-red-300 !py-3 justify-start flex-col items-start gap-2"
          data-testid="run-creator-rising"
        >
          <div className="flex items-center gap-2 w-full">
            {runningCreator ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flame className="h-4 w-4 text-red-400" />}
            <span className="font-display font-bold">Send creator alerts</span>
          </div>
          <span className="text-[11px] text-white/40 leading-snug">Emails each creator whose film ranks in this week&apos;s public Rising rail. Safe to run daily.</span>
        </button>
        <button
          onClick={runDigest}
          disabled={runningDigest}
          className="ghost-btn hover:border-[#E65F2B]/50 hover:text-[#E65F2B] !py-3 justify-start flex-col items-start gap-2"
          data-testid="run-weekly-digest"
        >
          <div className="flex items-center gap-2 w-full">
            {runningDigest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 text-[#E65F2B]" />}
            <span className="font-display font-bold">Force-send weekly digest</span>
          </div>
          <span className="text-[11px] text-white/40 leading-snug">Overrides the Sunday-only guard. Sends the top-3 rising films to every active subscriber.</span>
        </button>
      </div>

      {/* Log */}
      <p className="text-eyebrow mb-3">Recent activity</p>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-white/40 py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : log.length === 0 ? (
        <div className="text-sm text-white/40 py-4" data-testid="notifications-empty">
          No notifications sent yet. First creator alerts fire when films start hitting the Rising rail; digest fires Sunday.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="notifications-log">
          {log.map((n) => (
            <div key={n.id} className="flex items-center justify-between gap-3 py-2 px-3 rounded-md bg-white/[0.02] border border-white/5" data-testid={`log-row-${n.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {n.type === "creator_rising" ? (
                  <Flame className="h-3.5 w-3.5 text-red-400 shrink-0" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 text-[#EAB308] shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-sm truncate">
                    {n.type === "creator_rising" ? (
                      <>
                        <span className="text-white/80">Creator alert</span>{" "}
                        <span className="text-white/40">→</span>{" "}
                        <span className="text-white/70">{n.recipient}</span>{" "}
                        {n.is_new && <span className="badge-pill text-[10px] bg-red-500/10 text-red-300 border border-red-500/25 ml-1">NEW</span>}
                        {typeof n.rank === "number" && <span className="text-white/50 ml-1">· rank #{n.rank}</span>}
                      </>
                    ) : (
                      <>
                        <span className="text-white/80">Weekly digest</span>{" "}
                        <span className="text-white/40">·</span>{" "}
                        <span className="text-white/70">{n.sent_count || 0} sent</span>
                        {n.failed_count > 0 && <span className="text-red-400/70 ml-1">· {n.failed_count} failed</span>}
                        <span className="text-white/40 ml-1">· {n.film_count || 0} films</span>
                      </>
                    )}
                  </p>
                  <p className="text-[11px] text-white/30">{n.iso_week} · {new Date(n.sent_at).toLocaleString()}</p>
                </div>
              </div>
              <Send className="h-3 w-3 text-white/20 shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
